import React, { useCallback, useMemo } from 'react';
import { useAsync } from 'react-async';

import LoadingContainer from '../../../../components/LoadingContainer';
import { api } from '../../../../core/api';
import { CompanySearchFilters } from '../../../../generated/model/companySearchFilters';
import { CompanySearchRequest } from '../../../../generated/model/companySearchRequest';
import { useCompaniesState } from '../../shared/CompanyContext/CompanyContext';
import { useErrorsDispatch } from '../../shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../shared/ErrorContext/ErrorContext.types';
import { useFilterState } from '../../shared/FinancialDataContext/FinancialDataContext';
import { SearchSummaryDataTable } from './SearchSummaryDataTable';

interface Props {
  withCounts?: boolean;
}

const SearchSummaryTable: React.FC<Props> = ({ withCounts = true }) => {
  const { requestFiltersData, historySearch } = useFilterState();
  const dispatchErrors = useErrorsDispatch();
  const selectedCompanies = useCompaniesState();

  const modifySelectedCompanies = () => {
    const uidList: string[] = [];
    selectedCompanies.selectedCompanies.forEach((id) => {
      uidList.push('uid:' + id);
    });
    return uidList;
  };

  const modifiedSelectedCompanies = modifySelectedCompanies();

  const modifiedRequestFiltersData: CompanySearchFilters = useMemo(() => {
    if (modifiedSelectedCompanies.length === 0) {
      return requestFiltersData;
    }
    return {
      ...requestFiltersData,
      infoCompanyIdNumber: {
        value: modifiedSelectedCompanies,
      },
    };
  }, [modifiedSelectedCompanies, requestFiltersData]);

  const requestData: CompanySearchRequest = {
    filters: modifiedRequestFiltersData,
    historySearch,
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const promiseFn = useCallback(() => api.companySearch.searchSummary(requestData), []);
  const { data, isLoading } = useAsync({
    promiseFn,
    onReject: (error) => {
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: { error },
      });
      return Promise.reject(error);
    },
  });

  if (isLoading) {
    return <LoadingContainer />;
  }

  return <SearchSummaryDataTable data={data?.searchCriteria || []} withCounts={withCounts} />;
};

export default SearchSummaryTable;
